import React, { useMemo } from "react";
import { Card, Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DashboardCard from "../components/DashboardCard";
import { colors } from "../components/Theme/WebTheme";
import { FONT_FAMILY, WEB_SECONDORY_COLOR, formatUserAmount } from "../common/sharedFunctions";
import { computePartnerEarningsSummary } from "../common/partnerEarnings";

export default function DriverPartnerEarnings({ data }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector((state) => state.settingsdata.settings);
  const bookings = useSelector((state) => state.bookinglistdata.bookings);
  const SECONDORY_COLOR = WEB_SECONDORY_COLOR(settings?.appCat);

  const summary = useMemo(
    () => computePartnerEarningsSummary(bookings, data?.id, settings),
    [bookings, data?.id, settings]
  );

  const rows = [
    { key: "week", label: t("this_week_text", { defaultValue: "Esta semana" }) },
    { key: "month", label: t("this_month_text", { defaultValue: "Este mes" }) },
    { key: "quarter", label: t("this_quarter_text", { defaultValue: "Este trimestre" }) },
    { key: "year", label: t("this_year_text", { defaultValue: "Este año" }) },
    { key: "lifetime", label: t("lifetime_total", { defaultValue: "Total histórico" }) },
  ];

  if (!data?.id) return null;

  return (
    <Card
      style={{
        borderRadius: "19px",
        marginTop: 20,
        marginBottom: 20,
        padding: 20,
        boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
      }}
    >
      <Typography
        style={{
          fontFamily: FONT_FAMILY,
          fontSize: 22,
          fontWeight: 700,
          marginBottom: 16,
          textAlign: isRTL === "rtl" ? "right" : "left",
        }}
      >
        {t("partner_earnings_title", { defaultValue: "Rendimiento del socio" })}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <DashboardCard
            crdStyle={{ backgroundColor: colors.WALLET_CARD, color: colors.WHITE }}
            title={t("partner_platform_utility", { defaultValue: "Utilidad para la plataforma" })}
            subtitle={t("this_month_text", { defaultValue: "Este mes" })}
            icon={<AttachMoneyIcon style={{ color: colors.WHITE }} />}
          >
            {formatUserAmount(summary.month.platform, settings)}
          </DashboardCard>
        </Grid>
        <Grid item xs={12} sm={6}>
          <DashboardCard
            crdStyle={{ backgroundColor: colors.GREEN, color: colors.WHITE }}
            title={t("partner_driver_earnings", { defaultValue: "Ganancia del socio" })}
            subtitle={t("this_month_text", { defaultValue: "Este mes" })}
            icon={<TrendingUpIcon style={{ color: colors.WHITE }} />}
          >
            {formatUserAmount(summary.month.driver, settings)}
          </DashboardCard>
        </Grid>
      </Grid>

      <TableContainer
        style={{
          borderRadius: 12,
          boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
          direction: isRTL === "rtl" ? "rtl" : "ltr",
        }}
      >
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: colors.Table_Header || colors.CAR_BOX_EVEN }}>
              <TableCell style={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>
                {t("period", { defaultValue: "Periodo" })}
              </TableCell>
              <TableCell align="right" style={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>
                {t("partner_platform_utility", { defaultValue: "Utilidad plataforma" })}
              </TableCell>
              <TableCell align="right" style={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>
                {t("partner_driver_earnings", { defaultValue: "Ganancia socio" })}
              </TableCell>
              <TableCell align="right" style={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>
                {t("booking_count", { defaultValue: "Servicios" })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const bucket = summary[row.key];
              return (
                <TableRow key={row.key} hover>
                  <TableCell style={{ fontFamily: FONT_FAMILY, fontWeight: row.key === "lifetime" ? 700 : 500 }}>
                    {row.label}
                  </TableCell>
                  <TableCell align="right" style={{ fontFamily: FONT_FAMILY }}>
                    {formatUserAmount(bucket.platform, settings)}
                  </TableCell>
                  <TableCell align="right" style={{ fontFamily: FONT_FAMILY, color: colors.GREEN, fontWeight: 600 }}>
                    {formatUserAmount(bucket.driver, settings)}
                  </TableCell>
                  <TableCell align="right" style={{ fontFamily: FONT_FAMILY }}>
                    {bucket.rides}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        style={{
          fontFamily: FONT_FAMILY,
          fontSize: 12,
          color: colors.CARD_LABEL,
          marginTop: 12,
          textAlign: isRTL === "rtl" ? "right" : "left",
        }}
      >
        {t("partner_earnings_hint", {
          defaultValue:
            "Utilidad plataforma = cargo por servicio menos descuentos. Ganancia socio = parte del conductor en servicios completados o pagados.",
        })}
      </Typography>
    </Card>
  );
}
